import React, { useState, useEffect } from 'react';
import campaignService from '../../../services/campaignService';
import { useAuth } from '../../../hooks/useAuth';
import Icon from '../../../components/AppIcon';
import CampaignCreationForm from './CampaignCreationForm';
import { getAllTemplates } from '../../../constants/emailTemplates';

/**
 * Marketing Campaigns Tab Component
 * Displays and manages marketing email campaigns
 */
const MarketingCampaignsTab = () => {
  const { userProfile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCampaigns();
    loadStats();
  }, [filterStatus]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const filters = filterStatus !== 'all' ? { status: filterStatus } : {};
      const result = await campaignService.getCampaigns(filters);
      
      if (result.success) {
        setCampaigns(result.data);
      } else {
        console.error('Error loading campaigns:', result.error);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await campaignService.getCampaignStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateCampaign = () => {
    setSelectedCampaign(null);
    setShowCreationForm(true);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setShowCreationForm(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
      return;
    }

    const result = await campaignService.deleteCampaign(campaignId);
    if (result.success) {
      loadCampaigns();
      loadStats();
    } else {
      alert(`Error deleting campaign: ${result.error}`);
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to send this campaign? Emails will be sent to all recipients immediately.')) {
      return;
    }

    setLoading(true);
    const result = await campaignService.sendCampaign(campaignId);
    setLoading(false);

    if (result.success) {
      alert(`Campaign sent successfully!\n\nSent: ${result.data.sentCount}\nFailed: ${result.data.failedCount}\nTotal: ${result.data.totalRecipients}`);
      loadCampaigns();
      loadStats();
    } else {
      alert(`Error sending campaign: ${result.error}`);
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled campaign?')) {
      return;
    }

    const result = await campaignService.cancelCampaign(campaignId);
    if (result.success) {
      loadCampaigns();
      loadStats();
    } else {
      alert(`Error cancelling campaign: ${result.error}`);
    }
  };

  const handleFormClose = (shouldRefresh) => {
    setShowCreationForm(false);
    setSelectedCampaign(null);
    if (shouldRefresh) {
      loadCampaigns();
      loadStats();
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled' },
      sending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Sending' },
      sent: { bg: 'bg-green-100', text: 'text-green-800', label: 'Sent' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const filteredCampaigns = campaigns.filter(campaign => 
    campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Campaigns</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCampaigns}</p>
              </div>
              <Icon name="Mail" size={32} className="text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Emails Sent</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalEmailsSent}</p>
              </div>
              <Icon name="Send" size={32} className="text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Scheduled</p>
                <p className="text-2xl font-bold text-blue-600">{stats.scheduledCampaigns}</p>
              </div>
              <Icon name="Clock" size={32} className="text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{stats.totalEmailsFailed}</p>
              </div>
              <Icon name="AlertCircle" size={32} className="text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Header and Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Marketing Campaigns</h2>
            <p className="text-sm text-gray-600 mt-1">
              Create and manage email campaigns for users who opted in to marketing emails
            </p>
          </div>
          <button
            onClick={handleCreateCampaign}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={18} />
            New Campaign
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sent">Sent</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-gray-600 mt-4">Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Mail" size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No campaigns found</p>
            <button
              onClick={handleCreateCampaign}
              className="mt-4 text-primary hover:underline"
            >
              Create your first campaign
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCampaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{campaign.title}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Subject:</span> {campaign.subject}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Icon name="Users" size={14} />
                        {campaign.recipient_count || 0} recipients
                      </span>
                      {campaign.sent_count > 0 && (
                        <span className="flex items-center gap-1 text-green-600">
                          <Icon name="Check" size={14} />
                          {campaign.sent_count} sent
                        </span>
                      )}
                      {campaign.failed_count > 0 && (
                        <span className="flex items-center gap-1 text-red-600">
                          <Icon name="X" size={14} />
                          {campaign.failed_count} failed
                        </span>
                      )}
                      {campaign.scheduled_at && (
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={14} />
                          {new Date(campaign.scheduled_at).toLocaleString()}
                        </span>
                      )}
                      {campaign.sent_at && (
                        <span className="flex items-center gap-1">
                          <Icon name="Send" size={14} />
                          Sent {new Date(campaign.sent_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 ml-4">
                    {campaign.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleEditCampaign(campaign)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit campaign"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button
                          onClick={() => handleSendCampaign(campaign.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Send now"
                        >
                          <Icon name="Send" size={18} />
                        </button>
                      </>
                    )}
                    {campaign.status === 'scheduled' && (
                      <button
                        onClick={() => handleCancelCampaign(campaign.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel scheduled"
                      >
                        <Icon name="X" size={18} />
                      </button>
                    )}
                    {(campaign.status === 'draft' || campaign.status === 'cancelled') && (
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete campaign"
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Creation/Edit Form Modal */}
      {showCreationForm && (
        <CampaignCreationForm
          campaign={selectedCampaign}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default MarketingCampaignsTab;
